<script lang="ts" module>
	export const features = [
		{
			title: 'Design Your Unique Travel Guides',
			description: `
			Shape your journeys into comprehensive guides, enriching them with your personal insights and tips.
				`,
			image: '/visuals/icons/design.svg',
			screen: TripScreen,
		},
		{
			title: 'List Your Guides for Sale',
			description: `
			Share your guides with your eager audience, who are searching for a way to create detailed itineraries based on your recommendations.
				`,
			image: '/visuals/icons/shop.svg',
			screen: ListingScreen,
		},
		{
			title: 'Travelers Purchase Your Guide and Create Itineraries',
			description: `
			Observe as each purchase leads to a customized journey, with your guide at the heart of travelers' experiences.
				`,
			image: '/visuals/icons/touch.svg',
			screen: PanelScreen,
		},
		// {
		// 	title: 'Receive Payouts For Sales',
		// 	description: `
		// 			After purchase, travelers unlock your expertly curated destination highlights and tips,
		// 			offering them a comprehensive view of what life is like on-location.
		// 		`,
		// 	image: '/visuals/icons/money-fund-cycle.svg',
		// 	screen: HighlightScreen,
		// },
	]

	export const maxZIndex = 2147483647

	export const bodyVariantBackwards: Variant = {
		opacity: 0.4,
		scale: 0.8,
		zIndex: 0,
		filter: 'blur(4px)',
		transition: { duration: 0.4 },
	}

	export function bodyVariantForwards(changeCount: number) {
		return {
			y: '100%',
			zIndex: maxZIndex - changeCount,
			transition: { duration: 0.4 },
		} as Variant
	}

	export function bodyAnimation(isForwards: boolean, changeCount: number) {
		return {
			initial: 'initial',
			animate: 'animate',
			exit: 'exit',
			variants: {
				initial: true ? bodyVariantForwards(changeCount) : bodyVariantBackwards,
				animate: {
					y: '0%',
					opacity: 1,
					scale: 1,
					zIndex: maxZIndex / 2 - changeCount,
					filter: 'blur(0px)',
					transition: { duration: 0.4 },
				},
				exit: true ? bodyVariantBackwards : bodyVariantForwards(changeCount),
			} as MotionProps,
		}
	}
</script>

<!-- FeatureTabs.svelte -->
<script lang="ts">
	import TabGroup from '$lib/components/display/TabGroup.svelte'
	import TabList from '$lib/components/display/TabList.svelte'
	import Tab from '$lib/components/display/Tab.svelte'
	import TabPanels from '$lib/components/display/TabPanels.svelte'
	import TabPanel from '$lib/components/display/TabPanel.svelte'
	import PhoneFrame from './PhoneFrame.svelte'
	import CircleBackground from './CircleBackground.svelte'
	import { Motion } from 'svelte-motion'
	import type { Variant, MotionProps } from 'svelte-motion'
	import TripScreen from './TripScreen.svelte'
	import ListingScreen from './ListingScreen.svelte'
	import PanelScreen from './PanelScreen.svelte'

	const maxZIndex = 2147483647

	function bodyVariantForwards(changeCount: number) {
		return {
			y: '100%',
			zIndex: maxZIndex - changeCount,
			transition: { duration: 0.4 },
		} as Variant
	}

	function bodyAnimation(isForwards: boolean, changeCount: number) {
		return {
			initial: 'initial',
			animate: 'animate',
			exit: 'exit',
			variants: {
				initial: bodyVariantForwards(changeCount),
				animate: {
					y: '0%',
					opacity: 1,
					scale: 1,
					zIndex: maxZIndex / 2 - changeCount,
					filter: 'blur(0px)',
					transition: { duration: 0.4 },
				},
				exit: bodyVariantForwards(changeCount),
			} as MotionProps,
		}
	}

	let selectedIndex = $state(0)
	let prevIndex = $state(0)
	let changeCount = $state(0)
	let isForwards = $state(true)

	function handleUpdate(index: number) {
		prevIndex = selectedIndex
		selectedIndex = index
		isForwards = selectedIndex > prevIndex
		changeCount++
	}
</script>

<TabGroup
	vertical
	class="grid grid-cols-12 items-center gap-8 lg:gap-16 xl:gap-24"
	bind:selectedIndex
	onchange={handleUpdate}
>
	<div class="relative col-span-6">
		<div class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
			<CircleBackground color="#FF006E" id="circle-bg-features" />
		</div>
		<PhoneFrame class="z-10 mx-auto w-full h-full max-w-[366px]">
			<TabPanels class="h-full">
				{#each features as feature, idx}
					<TabPanel
						index={idx}
						class="col-start-1 row-start-1 flex h-full focus:outline-offset-[32px] ui-not-focus-visible:outline-none"
					>
						<feature.screen {...bodyAnimation(isForwards, idx)} />
					</TabPanel>
				{/each}
			</TabPanels>
		</PhoneFrame>
	</div>

	<TabList class="flex flex-col relative order-last col-span-6 space-y-6 text-white">
		{#each features as feature, idx}
			<Tab index={idx} class="relative hover:bg-brand-gray-5/80 rounded-2xl p-8">
				{#if idx === selectedIndex}
					<Motion
						layoutId="highlighted"
						initial={false}
						animate={{ backgroundColor: 'rgb(47 45 45 / .8)' }}
					>
						{#snippet children({ motion })}
							<div class="absolute inset-0 rounded-2xl" use:motion></div>
						{/snippet}
					</Motion>
				{/if}
				<div class="bg-transparent z-20 relative flex flex-col items-start justify-start">
					<img src={feature.image} alt="" class="size-8 opacity-70" />
					<h3 class="mt-6 text-lg font-semibold">{feature.title}</h3>
					<p class="mt-2 text-sm text-left text-brand-gray-3">{feature.description}</p>
				</div>
			</Tab>
		{/each}
	</TabList>
</TabGroup>
